import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box } from '@cbhq/cds-web/layout';
import { Spinner } from '@cbhq/cds-web/loaders';
import { ActionType, ComponentType, logEvent } from '@cbhq/client-analytics';

import { useOrganization } from '../../../contexts/OrganizationContext';
import { useExperiments } from '../../../utils/experiments';
import { useManagePermissionsNow } from '../hooks/useManagePermissionsNow';
import { useWorkspaceTeam } from '../hooks/useWorkspaceTeam';
import { canManageEntity, canManageMembers } from '../utils/canManageMembers';
import type { Member as TeamMember } from '../utils/constants';
import { ROUTE_TEAM } from '../utils/constants';
import { groupRolesByEntity, toTeamMember } from '../utils/workspaceTeam';

import { MultiLEEditTray } from './MultiLEEditTray';
import { SingleLEEditTray } from './SingleLEEditTray';

export function EditMember() {
  const { user_uuid: userUuid } = useParams<{ user_uuid: string }>();
  const navigate = useNavigate();
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.organizationId ?? '';
  // The trays snapshot their initial selections from experiment-gated seeding
  // (t0018_4fba688a), so they mount only once CIFER assignments have loaded.
  const { isLoaded: areExperimentsLoaded } = useExperiments();

  const {
    membersById,
    currentMember: currentUser,
    isLoaded: areMembersLoaded,
  } = useWorkspaceTeam(organizationId);

  const member = (userUuid && membersById.get(userUuid)) || null;

  // The role pickers and the role writers in useTeamApi read effective roles only.
  // Pending roles reach just the pending role banner, and "Revoke admin access"
  // cancels them.
  const memberRolesForEntity = useMemo(
    () => (member ? groupRolesByEntity(member.effectiveRoles) : undefined),
    [member],
  );

  // IDFE-4022: an admin role still in its grace period doesn't grant manage controls.
  const managePermissionsNow = useManagePermissionsNow(currentUser, organizationId);

  const canEdit = useMemo(
    () => canManageMembers(currentUser, managePermissionsNow),
    [currentUser, managePermissionsNow],
  );

  const canEditEntity = useCallback(
    (legalEntityId: string) => canManageEntity(currentUser, legalEntityId, managePermissionsNow),
    [currentUser, managePermissionsNow],
  );

  const teamMember = useMemo<TeamMember | null>(
    () => (member ? toTeamMember(member) : null),
    [member],
  );

  const handleClose = useCallback(() => {
    logEvent('member_info_page_closed', {
      componentType: ComponentType.modal,
      action: ActionType.dismiss,
    });
    navigate(ROUTE_TEAM);
  }, [navigate]);

  useEffect(() => {
    logEvent('member_info_page_viewed', {
      componentType: ComponentType.page,
      action: ActionType.view,
    });
  }, []);

  // Redirect if organizationId is missing
  useEffect(() => {
    if (!organizationId) {
      navigate({ pathname: ROUTE_TEAM }, { replace: true });
    }
  }, [organizationId, navigate]);

  // Redirect if member not found once data has loaded
  useEffect(() => {
    if (areMembersLoaded && !member) {
      navigate(ROUTE_TEAM, { replace: true });
    }
  }, [areMembersLoaded, member, navigate]);

  // areMembersLoaded also waits for the user profile, so canEdit is final.
  if (!teamMember || !areExperimentsLoaded || !areMembersLoaded) {
    return (
      <Box alignItems="center" justifyContent="center" padding={6}>
        <Spinner size={4} color="fgPrimary" />
      </Box>
    );
  }

  const legalEntities = selectedOrganization?.legalEntities ?? [];

  if (legalEntities.length > 1) {
    return (
      <MultiLEEditTray
        member={teamMember}
        memberRolesForEntity={memberRolesForEntity}
        pendingRoles={member?.pendingRoles}
        status="active"
        isEditable={canEdit}
        canEditEntity={canEditEntity}
        onClose={handleClose}
      />
    );
  }

  const singleEntityId = legalEntities[0]?.legalEntityId;

  return (
    <SingleLEEditTray
      member={teamMember}
      status="active"
      isEditable={canEdit && singleEntityId != null && canEditEntity(singleEntityId)}
      pendingRoles={member?.pendingRoles}
      canEditEntity={canEditEntity}
      onClose={handleClose}
    />
  );
}
